<template>
    <div
        v-editable="blok"
        class="articles-slider"
    >
        <KSectionHeader
            class="articles-slider__header"
            :eyebrow="blok.eyebrow"
            :text="blok.subtitle"
            :align="blok.align || 'start'"
        >
            <template
                v-if="title"
                #title
            >
                <span v-html="title" />
            </template>
            <template
                v-if="blok.action_label && (blok.action_link?.url || blok.action_link?.email || blok.action_link?.cached_url || blok.action_link?.anchor)"
                #actions
            >
                <StoryblokSectionHeaderAction
                    :label="blok.action_label"
                    :link="blok.action_link"
                    placement="articles-slider"
                />
            </template>
        </KSectionHeader>
        <div
            v-if="slides.length"
            class="articles-slider__grid"
        >
            <div
                v-for="story in slides"
                :key="story.uuid"
            >
                <KTeaserCard
                    :as="NuxtLink"
                    :to="`/${story.full_slug}`"
                    variant="overlay"
                    title-as="h2"
                    @click="capture('related_article_clicked', {
                        placement: 'articles-slider',
                        target_slug: story.full_slug,
                    })"
                >
                    <template #media>
                        <KFrame
                            ratio="1"
                            scrim
                        >
                            <NuxtImg
                                :src="story.content.image.filename"
                                :alt="story.content.image.alt"
                                width="440"
                                height="440"
                                fit="cover"
                                format="auto"
                                loading="lazy"
                                sizes="xs:46vw sm:46vw md:310px lg:290px"
                            />
                        </KFrame>
                    </template>
                    <template #title>
                        <span
                            v-if="story.content.title"
                            v-html="renderHighlightedText(story.content.title)"
                        />
                        <template v-else>
                            {{ story.name }}
                        </template>
                    </template>
                </KTeaserCard>
            </div>
        </div>
    </div>
</template>

<script setup>
import { KFrame, KSectionHeader, KTeaserCard } from '@vemedia/karibu'
import { resolveComponent } from 'vue'
import renderHighlightedText from '~/helpers/renderHighlightedText'

const { blok } = defineProps({
    blok: {
        type: Object,
        default: () => ({}),
    },
})

const { capture } = useTracking()
const NuxtLink = resolveComponent('NuxtLink')

// Fix storyblock rich text to string
const title = computed(() => renderHighlightedText(blok.title))
const articleUuids = blok.articles.join(',') || ''

const { data: stories } = await useAsyncData(`articles-slider-${blok._uid}-new`, async () => {
    return await $fetch(`/api/by-uuids?id=${blok._uid}&uuids=${articleUuids}`)
})

// Guard against a null / empty API result so an empty `stories` can never
// render a null vnode.
const slides = computed(() => (Array.isArray(stories.value) ? stories.value.filter(Boolean) : []))
</script>

<style lang="scss">
@use '@vemedia/bcn-components/scss/responsive' as *;

.articles-slider {
    &__header {
        margin-bottom: var(--space-6);
    }

    &__grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: var(--space-5);

        @include tablet {
            grid-template-columns: repeat(3, 1fr);
        }

        @include desktop {
            grid-template-columns: repeat(4, 1fr);
        }

        .u-highlight {
            color: inherit;
        }
    }
}
</style>
